import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import { BookContext, BookCover } from '@astrid/components'
import useThunkDispatch from 'hooks/useThunkDispatch'
import BookFactory from 'components/BookFactory/BookFactory'
import { routes } from 'routes/RouterStackComponent'
import { getExercisesByBookId } from 'store/services/Exercise'
import {
  BookMode,
  asyncCreateProgressEntity,
  asyncGetAllBooks,
  asyncGetBooksProgress,
  selectCurrentBook,
  setCurrentBookInfo
} from 'store/services/Book'
import DifficultWordsPractise from 'views/DifficultWordsPractise/DifficultWordsPractise'

import { InjectFavouriteButton } from './InjectFavouriteButton'
import './BookView.scss'

const BookView: React.FC<RouteComponentProps<{ id: string; bookMode: BookMode }>> = (props) => {
  const dispatch = useThunkDispatch()
  const bookId = props.match.params.id
  const bookMode = props.match.params.bookMode

  if (!Object.values(BookMode).includes(bookMode)) {
    // Return home if the book context is incorrect
    props.history.push(routes.home)
  }

  const [coverOpen, setCoverOpen] = useState(true)
  const [loading, setLoading] = useState(true)

  const { book, bookProgress } = useSelector(selectCurrentBook)

  const isListening = bookMode === BookMode.LISTEN
  const isReading = bookMode === BookMode.READ

  const onDidMount = () => {
    ;(async () => {
      try {
        dispatch(setCurrentBookInfo(bookId, bookMode))

        if (!book) {
          await dispatch(asyncGetAllBooks())
          await dispatch(asyncGetBooksProgress())
        }

        await dispatch(getExercisesByBookId(bookId))
        if (!bookProgress) await dispatch(asyncCreateProgressEntity(bookId))
      } finally {
        setLoading(false)
      }
    })()
  }

  useEffect(() => {
    onDidMount()
    // eslint-disable-next-line
  }, [])

  const onCompleteBook = () => {
    if (book && bookProgress) {
      props.history.replace(routes.congratulation, {
        book,
        bookMode
      })
    }
  }

  if (!book || !bookProgress) return null

  if (coverOpen && ((isReading && bookProgress.index === 0) || (isListening && bookProgress.indexListening === 0))) {
    return (
      <>
        <BookCover
          disableContinue={loading}
          book={book as any}
          bookContext={bookMode === BookMode.READ ? BookContext.reading : BookContext.listening}
          onStart={() => setCoverOpen(false)}
        />
        <InjectFavouriteButton bookMode={bookMode} bookProgress={bookProgress} />
      </>
    )
  }

  if (
    (bookProgress.index === book.exercises.length && !bookProgress.isCompleted) ||
    (bookProgress.indexListening === book.exercises.length && !bookProgress.isCompletedListening)
  ) {
    if (bookProgress.difficultWords.length > 0 && isReading) {
      return (
        <DifficultWordsPractise book={book} bookMode={bookMode} bookProgress={bookProgress} onFinish={onCompleteBook} />
      )
    } else {
      onCompleteBook()
      return null
    }
  }

  return <BookFactory book={book} bookProgress={bookProgress} onCompleteBook={onCompleteBook} bookMode={bookMode} />
}

export default withRouter(BookView)
